import { useCallback } from 'react';

import { isNumber } from 'lodash';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { countNotificationsHistories, getNotificationsHistories, FieldName } from 'admin/notifications/Notifications';
import {
    NotificationChannel,
    NotificationHistoryStatus,
    NotificationType,
    NotificationHistory,
} from 'admin/notifications/Notifications.types';
import { isAuthorized, useProfile } from 'auth/Auth';
import { SearchRequest, Operator } from 'core/util/Search';

export const NOTIFICATION_COUNT = 20;

export const useNotificationsHook = () => {
    const [{ profile }] = useProfile();

    const fetchInitialAllNotificationCount = useCallback(
        async (channel: NotificationChannel = NotificationChannel.InApp): Promise<number | undefined> => {
            const search: SearchRequest = {
                filters: [
                    { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                    { name: FieldName.NotificationHistoryViewed, values: ['false'] },
                    {
                        name: FieldName.NotificationHistoryChannel,
                        values: [NotificationChannel[channel]],
                    },
                ],
            };

            try {
                if (isAuthorized({ actions: [ActionId.PlatformNotifications.View] })) {
                    const result = await trackPromise(
                        countNotificationsHistories(profile?.tenant?.id, search),
                        'notifications-history-count',
                    );

                    return result.count;
                }
            } catch {
                // Silently handle
                return undefined;
            }
            return undefined;
        },
        [profile],
    );

    const fetchNotificationHistories = useCallback(
        async (
            tab: string,
            start?: number,
            count?: number,
            channel: NotificationChannel = NotificationChannel.InApp,
        ): Promise<NotificationHistory[]> => {
            const search: SearchRequest = {
                filters: [
                    { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                    {
                        name: FieldName.NotificationHistoryChannel,
                        values: [NotificationChannel[channel]],
                    },
                ],
                count: isNumber(count) ? count : NOTIFICATION_COUNT,
                start: isNumber(start) ? start : 0,
            };

            if (tab === 'unread') {
                search.filters.push(
                    { name: FieldName.NotificationHistoryStatus, values: [String(NotificationHistoryStatus.UnRead)] },
                    {
                        name: FieldName.NotificationHistoryType,
                        values: [NotificationType[NotificationType.DataCollectionStatus]],
                        operator: Operator.NotEqual,
                    },
                );
            }

            if (tab === 'remindMeLater') {
                search.filters.push({ name: FieldName.NotificationHistoryRemindedFlag, values: ['true'] });
            }

            const notifications = await trackPromise(
                getNotificationsHistories(profile?.tenant?.id, search),
                'notifications-history',
            );

            return notifications;
        },
        [profile],
    );

    const fetchNotificationHistoryById = useCallback(
        async (notificationHistoryId: string): Promise<NotificationHistory | undefined> => {
            const search: SearchRequest = {
                filters: [
                    { name: FieldName.NotificationHistoryID, values: [notificationHistoryId] },
                    { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                ],
                count: 1,
                start: 0,
            };

            const results = await getNotificationsHistories(profile?.tenant?.id, search);

            return results?.[0];
        },
        [profile],
    );

    const fetchNotificationHistoryByNotificationId = useCallback(
        async (notificationId: string): Promise<NotificationHistory | undefined> => {
            const search: SearchRequest = {
                filters: [
                    { name: FieldName.NotificationHistoryNotificationID, values: [notificationId] },
                    { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                ],
                count: 1,
                start: 0,
            };
            const results = await getNotificationsHistories(profile?.tenant?.id, search);

            return results?.[0];
        },
        [profile],
    );

    const fetchNotificationHistoryByDeeplinkOrHistoryId = useCallback(
        async (params: {
            notificationHistoryId?: string | null;
            notificationId?: string | null;
            deeplinkId?: string | null;
        }): Promise<NotificationHistory | undefined> => {
            const { notificationHistoryId, notificationId, deeplinkId } = params;

            // Path 1 — direct history ID lookup
            if (notificationHistoryId) {
                const search: SearchRequest = {
                    filters: [
                        { name: FieldName.NotificationHistoryID, values: [notificationHistoryId] },
                        { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                    ],
                    count: 1,
                    start: 0,
                };
                const results = await getNotificationsHistories(profile?.tenant?.id, search);

                return results?.[0];
            }

            // Path 2 — notificationId + deeplinkId lookup
            if (notificationId && deeplinkId) {
                const search: SearchRequest = {
                    filters: [
                        { name: FieldName.NotificationHistoryNotificationID, values: [notificationId] },
                        { name: FieldName.NotificationHistoryDeeplinkID, values: [deeplinkId] },
                        { name: FieldName.NotificationHistoryUserID, values: [String(profile?.user?.id)] },
                    ],
                    count: 1,
                    start: 0,
                };
                const results = await getNotificationsHistories(profile?.tenant?.id, search);

                return results?.[0];
            }

            return undefined;
        },
        [profile],
    );

    return {
        fetchInitialAllNotificationCount,
        fetchNotificationHistories,
        fetchNotificationHistoryById,
        fetchNotificationHistoryByNotificationId,
        fetchNotificationHistoryByDeeplinkOrHistoryId,
    };
};
